import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Mic, Square, RotateCcw, Send, CheckCircle2, AlertCircle, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import KineticMarquee from "@/components/KineticMarquee";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_SECONDS = 60;

export default function Voicemail() {
  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioElRef = useRef(null);

  const [state, setState] = useState("idle"); // idle | requesting | ready | recording | recorded | sending | sent | error
  const [error, setError] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [blob, setBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    return () => cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanup = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
    if (audioUrl) URL.revokeObjectURL(audioUrl);
  };

  const requestMic = async () => {
    setError("");
    setState("requesting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      setState("ready");
    } catch (e) {
      setError(
        "Microphone permission denied. Please allow microphone access in your browser settings and try again."
      );
      setState("error");
    }
  };

  const startRecording = () => {
    if (!mediaStreamRef.current) return;
    chunksRef.current = [];
    let mimeType = "audio/webm";
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported) {
      if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
        mimeType = "audio/webm;codecs=opus";
      } else if (MediaRecorder.isTypeSupported("audio/mp4")) {
        mimeType = "audio/mp4";
      }
    }
    const rec = new MediaRecorder(mediaStreamRef.current, { mimeType });
    rec.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    rec.onstop = () => {
      const finalBlob = new Blob(chunksRef.current, { type: mimeType });
      setBlob(finalBlob);
      const url = URL.createObjectURL(finalBlob);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      setAudioUrl(url);
      setState("recorded");
    };
    mediaRecorderRef.current = rec;
    rec.start();
    setSeconds(0);
    setState("recording");
    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s + 1 >= MAX_SECONDS) {
          stopRecording();
        }
        return s + 1;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const resetRecording = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setBlob(null);
    setAudioUrl(null);
    setSeconds(0);
    setState("ready");
  };

  const submit = async () => {
    if (!blob) return;
    setState("sending");
    setError("");
    const fd = new FormData();
    fd.append("audio", blob, `voicemail.${blob.type.includes("mp4") ? "m4a" : "webm"}`);
    if (name) fd.append("from_name", name);
    if (email) fd.append("from_email", email);
    if (message) fd.append("message", message);
    fd.append("duration_sec", String(seconds));
    try {
      await axios.post(`${API}/voicemails`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Voicemail sent. Cheers!");
      setState("sent");
      // free mic
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
      }
    } catch (e) {
      const msg =
        e?.response?.data?.detail || e?.message || "Couldn't send. Try again.";
      setError(typeof msg === "string" ? msg : "Submission failed.");
      toast.error("Couldn't send voicemail.");
      setState("recorded");
    }
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const remaining = MAX_SECONDS - seconds;

  return (
    <div data-testid="voicemail-page" className="px-6 lg:px-12 py-16 lg:py-24 pb-32 text-[var(--tsws-green)]">
      <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
        <div className="lg:col-span-8">
          <span className="font-label text-xs text-[var(--tsws-cigar)]">⏤ Leave A Voicemail</span>
          <h1 className="font-display text-7xl md:text-8xl lg:text-[10rem] mt-3 leading-[0.82]">
            speak<br />
            your piece<span className="text-[var(--tsws-cigar)]">.</span>
          </h1>
        </div>
        <div className="lg:col-span-4">
          <p className="font-tag text-xl">60 seconds. One take.</p>
          <p className="mt-3 leading-relaxed opacity-85">
            Got a question, a hot take, a story Sav needs to hear? Hit record. The juicy ones make
            it on the show.
          </p>
        </div>
      </div>

      <KineticMarquee phrases={["hit record", "60 seconds", "no edits", "send it"]} />

      <div className="mt-16 grid lg:grid-cols-12 gap-12">
        {/* Recorder */}
        <div className="lg:col-span-7">
          <div className="border-2 border-[var(--tsws-green)] bg-[var(--tsws-paper)] p-8 lg:p-12 shadow-brutal">
            {state === "sent" ? (
              <div data-testid="voicemail-success" className="text-center py-10">
                <CheckCircle2 className="mx-auto text-[var(--tsws-green)]" size={64} />
                <h2 className="font-display text-5xl mt-6">message sent.</h2>
                <p className="font-tag text-xl mt-3">Cheers for that. Sav will hear it.</p>
                <button
                  data-testid="record-another-btn"
                  onClick={() => {
                    setBlob(null);
                    setAudioUrl(null);
                    setSeconds(0);
                    setName("");
                    setEmail("");
                    setMessage("");
                    setState("idle");
                  }}
                  className="mt-8 font-display text-lg px-6 py-3 border-2 border-[var(--tsws-green)] hover:bg-[var(--tsws-green)] hover:text-[var(--tsws-orange)]"
                >
                  leave another →
                </button>
              </div>
            ) : (
              <>
                {/* State: idle */}
                {state === "idle" && (
                  <div data-testid="voicemail-idle" className="text-center py-6">
                    <Mic size={56} className="mx-auto text-[var(--tsws-cigar)]" />
                    <h2 className="font-display text-4xl mt-5">tap the mic.</h2>
                    <p className="opacity-80 mt-2 max-w-md mx-auto">
                      Your browser will ask for microphone permission. Allow it, then you'll get a
                      big record button.
                    </p>
                    <button
                      data-testid="enable-mic-btn"
                      onClick={requestMic}
                      className="mt-7 font-display text-xl px-8 py-4 border-2 border-[var(--tsws-green)] bg-[var(--tsws-green)] text-[var(--tsws-orange)] hover-brutal hover:bg-[var(--tsws-cigar)] hover:border-[var(--tsws-cigar)] inline-flex items-center gap-3"
                    >
                      <Mic size={18} /> enable microphone
                    </button>
                  </div>
                )}

                {state === "requesting" && (
                  <div className="text-center py-10">
                    <p className="font-tag text-xl">Waiting for mic permission...</p>
                  </div>
                )}

                {/* State: ready / recording */}
                {(state === "ready" || state === "recording") && (
                  <div className="text-center py-4" data-testid="voicemail-recorder">
                    <button
                      data-testid="record-btn"
                      onClick={state === "recording" ? stopRecording : startRecording}
                      className={`relative mx-auto h-44 w-44 rounded-full border-4 border-[var(--tsws-green)] flex items-center justify-center transition-all hover:scale-105 ${
                        state === "recording"
                          ? "bg-[var(--tsws-cigar)] animate-pulse"
                          : "bg-[var(--tsws-green)]"
                      }`}
                      aria-label={state === "recording" ? "Stop recording" : "Start recording"}
                    >
                      {state === "recording" ? (
                        <Square size={48} className="text-[var(--tsws-paper)]" fill="currentColor" />
                      ) : (
                        <Mic size={64} className="text-[var(--tsws-orange)]" />
                      )}
                    </button>
                    <div className="mt-6 font-display text-6xl tabular-nums">
                      {fmt(seconds)}{" "}
                      <span className="opacity-50 text-3xl">/ {fmt(MAX_SECONDS)}</span>
                    </div>
                    <p className="font-tag text-lg mt-2 opacity-85">
                      {state === "recording"
                        ? `${remaining}s remaining — auto-stops at 60s`
                        : "Tap to start recording (60s max)"}
                    </p>
                  </div>
                )}

                {/* State: recorded */}
                {(state === "recorded" || state === "sending") && (
                  <div data-testid="voicemail-playback" className="py-2">
                    <div className="flex items-center gap-3 mb-4">
                      <PlayCircle className="text-[var(--tsws-cigar)]" />
                      <h2 className="font-display text-3xl">have a listen first.</h2>
                    </div>
                    <audio
                      ref={audioElRef}
                      data-testid="voicemail-audio-preview"
                      src={audioUrl}
                      controls
                      className="w-full"
                    />
                    <p className="mt-2 font-tag text-base opacity-80">
                      Length: <span className="font-bold">{fmt(seconds)}</span>
                    </p>

                    <div className="mt-6 grid md:grid-cols-2 gap-4">
                      <label className="block">
                        <span className="block font-label text-xs mb-2 opacity-80">Your name (optional)</span>
                        <input
                          data-testid="vm-name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          maxLength={120}
                          className="brutal-input"
                          placeholder="Anonymous Aussie"
                        />
                      </label>
                      <label className="block">
                        <span className="block font-label text-xs mb-2 opacity-80">Reply email (optional)</span>
                        <input
                          data-testid="vm-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="brutal-input"
                          placeholder="you@example.com"
                        />
                      </label>
                    </div>
                    <label className="block mt-4">
                      <span className="block font-label text-xs mb-2 opacity-80">Note (optional)</span>
                      <textarea
                        data-testid="vm-message"
                        rows={2}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        maxLength={500}
                        className="brutal-input resize-y"
                        placeholder="Anything you want Sav to know before he hits play."
                      />
                    </label>

                    {error && (
                      <div data-testid="voicemail-error" className="mt-5 border-2 border-[var(--tsws-cigar)] bg-[var(--tsws-orange-light)] p-4 flex gap-2 items-start">
                        <AlertCircle className="text-[var(--tsws-cigar)] mt-0.5 shrink-0" size={18} />
                        <p>{error}</p>
                      </div>
                    )}

                    <div className="mt-7 flex flex-wrap gap-3 justify-between items-center">
                      <button
                        data-testid="vm-redo"
                        onClick={resetRecording}
                        disabled={state === "sending"}
                        className="font-display text-base px-5 py-3 border-2 border-[var(--tsws-green)] hover:bg-[var(--tsws-green)] hover:text-[var(--tsws-orange)] inline-flex items-center gap-2 disabled:opacity-50"
                      >
                        <RotateCcw size={16} /> re-record
                      </button>
                      <button
                        data-testid="vm-send"
                        onClick={submit}
                        disabled={state === "sending"}
                        className="font-display text-lg px-7 py-4 border-2 border-[var(--tsws-green)] bg-[var(--tsws-green)] text-[var(--tsws-orange)] hover-brutal hover:bg-[var(--tsws-cigar)] hover:border-[var(--tsws-cigar)] inline-flex items-center gap-3 disabled:opacity-60"
                      >
                        {state === "sending" ? "sending..." : "send voicemail"} <Send size={16} />
                      </button>
                    </div>
                  </div>
                )}

                {state === "error" && (
                  <div data-testid="voicemail-mic-error" className="text-center py-10">
                    <AlertCircle className="mx-auto text-[var(--tsws-cigar)]" size={56} />
                    <h2 className="font-display text-3xl mt-4">no mic, no message.</h2>
                    <p className="mt-3 max-w-md mx-auto opacity-85">{error}</p>
                    <button
                      onClick={requestMic}
                      className="mt-6 font-display text-base px-5 py-3 border-2 border-[var(--tsws-green)] hover:bg-[var(--tsws-green)] hover:text-[var(--tsws-orange)]"
                    >
                      try again →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Side info */}
        <aside className="lg:col-span-5 space-y-6">
          <div className="border-2 border-[var(--tsws-green)] bg-[var(--tsws-paper)] p-6">
            <div className="font-display text-2xl text-[var(--tsws-cigar)] mb-3">good voicemails are...</div>
            <ul className="space-y-2">
              <li>→ Short, sharp, one idea.</li>
              <li>→ Loud enough to hear. Quiet room helps.</li>
              <li>→ Honest. No script.</li>
              <li>→ Cheeky welcome. Inappropriate also welcome.</li>
            </ul>
          </div>
          <div className="border-2 border-[var(--tsws-green)] bg-[var(--tsws-cream)] p-6">
            <div className="font-display text-2xl mb-2">what happens next?</div>
            <p>
              Your voicemail goes straight to Sav's inbox. If it slaps, he plays it on the show.
              Leave a reply email if you want a personal response.
            </p>
          </div>
        </aside>
      </div>

      <style>{`
        .brutal-input {
          width: 100%;
          background: var(--tsws-orange);
          color: var(--tsws-green);
          border: 2px solid var(--tsws-green);
          padding: 12px 14px;
          font-family: 'Fraunces', Georgia, serif;
          font-size: 15px;
          outline: none;
          transition: background 200ms ease, color 200ms ease, box-shadow 200ms ease;
        }
        .brutal-input::placeholder { color: var(--tsws-green); opacity: 0.45; }
        .brutal-input:focus {
          background: var(--tsws-green);
          color: var(--tsws-orange);
          box-shadow: 4px 4px 0 0 var(--tsws-cigar);
        }
      `}</style>
    </div>
  );
}
